import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import SchemaOrg from '@/components/base/SchemaOrg';
import { useSEO } from '@/hooks/useSEO';
import { categories, comics } from '@/mocks/comics';

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '韩漫之家',
  url: 'http://bferj.cn/',
  inLanguage: 'zh-CN',
};

export default function CategoryPage() {
  const { slug = 'all' } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];

  const filteredComics = slug === 'all'
    ? comics
    : comics.filter((c) => c.category === slug);

  const pageTitle = slug === 'all'
    ? '漫画分类 - 全部韩国漫画 | 韩漫之家'
    : `${currentCategory.name} - 免费在线韩国漫画 | 韩漫之家`;

  const pageDesc = slug === 'all'
    ? '浏览韩漫之家全部韩国漫画分类，涵盖恋爱、奇幻、动作、悬疑、搞笑、剧情、恐怖、日常、古风等全品类韩漫资源，无遮挡下拉式免费在线阅读。'
    : `韩漫之家${currentCategory.name}频道，精选优质${currentCategory.name}作品，无遮挡下拉式免费在线阅读，每日更新热门韩国漫画。`;

  const breadcrumbItems = [
    { label: '漫画分类', href: '/category/all' },
    ...(slug !== 'all' ? [{ label: currentCategory.name }] : []),
  ];

  useSEO({
    title: pageTitle,
    description: pageDesc,
    keywords: slug === 'all'
      ? '韩漫分类, 免费韩国漫画, 漫画类型, 韩漫之家'
      : `${currentCategory.name}, 韩国漫画, 免费${currentCategory.name}, 韩漫之家`,
    canonicalUrl: `http://bferj.cn/category/${slug}`,
    ogTitle: pageTitle,
    ogDescription: pageDesc,
    ogType: 'website',
    twitterCard: 'summary_large_image',
  });

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://bferj.cn/' },
      { '@type': 'ListItem', position: 2, name: '漫画分类', item: 'http://bferj.cn/category/all' },
      ...(slug !== 'all' ? [{ '@type': 'ListItem', position: 3, name: currentCategory.name }] : []),
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg data={websiteSchema} />
      <SchemaOrg data={breadcrumbSchema} />

      <Navbar />

      <main className="pt-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
          <Breadcrumb items={breadcrumbItems} />

          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mt-6 mb-2">
            {slug === 'all' ? '全部漫画' : currentCategory.name}
          </h1>
          <p className="text-sm text-foreground-600 mb-6">
            {slug === 'all'
              ? '浏览韩漫之家全部韩国漫画，涵盖多种类型，每日更新'
              : `共 ${filteredComics.length} 部${currentCategory.name}作品，无遮挡下拉式免费阅读`}
          </p>

          {/* Category Filter */}
          <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-8 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  slug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-600'
                }`}
              >
                <i className={`${cat.icon} text-xs`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Comic Grid */}
          {filteredComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6" data-product-shop>
              {filteredComics.map((comic) => (
                <ComicCard key={comic.id} {...comic} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-600">该分类暂无漫画，请浏览其他分类</p>
              <Link to="/category/all" className="mt-4 inline-block text-primary-500 hover:text-primary-600 text-sm">
                查看全部漫画
              </Link>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}